import { useMemo, useState } from "react";
import { Pie, PieChart, Cell, Treemap, ResponsiveContainer } from "recharts";
import { PieChart as PieChartIcon, LayoutList, TrendingUp, Grid3X3 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  demoAllocationData,
  demoAllocationByType,
  demoAllocationTotal,
  demoSectorAllocationData,
  demoSectorTotal,
} from "@/lib/landing-demo-data";

type GroupByType = "asset" | "type";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const formatCompactCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

// Treemap content renderer (same pattern as SectorAllocationChart)
type TreemapContentProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  name: string;
  color: string;
  percentage?: number;
  payload?: { percentage?: number };
};

const CustomTreemapContent = (props: TreemapContentProps) => {
  const { x, y, width, height, name, color } = props;
  const percentage = props.percentage ?? props.payload?.percentage ?? 0;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: color,
          stroke: "hsl(var(--background))",
          strokeWidth: 2,
        }}
      />
      {width > 30 && height > 30 && (
        <foreignObject x={x} y={y} width={width} height={height}>
          <div className="w-full h-full p-1.5 flex flex-col items-center justify-center text-center gap-0.5 overflow-hidden">
            <span
              className="text-xs font-semibold text-white leading-tight line-clamp-2"
              title={name}
            >
              {name}
            </span>
            {height > 45 && (
              <span className="text-xs text-white/90 font-mono font-semibold">
                {percentage.toFixed(0)}%
              </span>
            )}
          </div>
        </foreignObject>
      )}
    </g>
  );
};

// Treemap tooltip
const TreemapTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    payload?: { name?: string; size?: number; percentage?: number };
    name?: string;
    value?: number;
  }>;
}) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0]?.payload;
  if (!entry) return null;

  const name = entry.name ?? payload[0]?.name;
  const value = typeof entry.size === "number" ? entry.size : payload[0]?.value;
  const percentage = typeof entry.percentage === "number" ? entry.percentage : 0;

  return (
    <div className="rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-sm shadow-xl">
      <div className="flex flex-col gap-1 min-w-[180px]">
        <span className="text-sm font-semibold">{name}</span>
        <div className="flex items-center gap-2">
          <span className="font-mono font-semibold text-sm">
            {formatCurrency(value as number)}
          </span>
          <span className="text-muted-foreground text-sm">
            {percentage.toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
};

export default function AllocationShowcase() {
  const [groupBy, setGroupBy] = useState<GroupByType>("asset");

  const { chartData, chartConfig } = useMemo(() => {
    const data = groupBy === "asset" 
      ? demoAllocationData.map((item) => ({
          name: item.symbol,
          fullName: item.name,
          value: item.value,
          percentage: (item.value / demoAllocationTotal) * 100,
          color: item.color,
        }))
      : demoAllocationByType.map((item) => ({
          name: item.name,
          fullName: item.name,
          value: item.value,
          percentage: item.percentage,
          color: item.color,
        }));

    const config: ChartConfig = {
      value: { label: "Value" },
    };
    
    data.forEach((item) => {
      config[item.name] = {
        label: item.fullName,
        color: item.color,
      };
    });

    return { chartData: data, chartConfig: config };
  }, [groupBy]);

  const sectorChartConfig = useMemo(() => {
    const config: ChartConfig = {
      value: { label: "Value" },
    };
    demoSectorAllocationData.forEach((item) => {
      config[item.name] = {
        label: item.name,
        color: item.color,
      };
    });
    return config;
  }, []);

  return (
    <section className="w-full py-20 bg-background">
      <div className="container px-4 md:px-6 mx-auto">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center space-y-4 mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest">
            <PieChartIcon className="w-3 h-3" />
            Portfolio Allocation
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-serif">
            Visualize Your{" "}
            <span className="text-primary">Asset Allocation</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl">
            Get a clear picture of how your investments are distributed across 
            different assets, types and sectors.
          </p>
        </div>

        {/* Two Cards Side by Side */}
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Card: Asset/Type Allocation (Donut) */}
            <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-xl">
              {/* Header with Toggle */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold">Allocation</h3>
                  <div className="flex items-center bg-muted/50 rounded-lg p-0.5">
                    <button
                      onClick={() => setGroupBy("asset")}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all",
                        groupBy === "asset"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <PieChartIcon className="h-3 w-3" />
                      By Asset
                    </button>
                    <button
                      onClick={() => setGroupBy("type")}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all",
                        groupBy === "type"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <LayoutList className="h-3 w-3" />
                      By Type
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs font-medium text-emerald-500">
                  <TrendingUp className="h-3 w-3" />
                  +12.4%
                </div>
              </div>

              {/* Chart + Legend stacked */}
              <div className="flex flex-col items-center gap-6">
                {/* Chart */}
                <div className="relative w-full max-w-[280px] aspect-square">
                  {/* Center label */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-20">
                    <span className="text-2xl font-bold tracking-tight">
                      {formatCompactCurrency(demoAllocationTotal)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {chartData.length} {groupBy === "asset" ? "assets" : "types"}
                    </span>
                  </div>
                  <ChartContainer config={chartConfig} className="w-full h-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <ChartTooltip
                          cursor={false}
                          content={
                            <ChartTooltipContent
                              hideLabel
                              formatter={(value, name, item) => {
                                const percentage = item.payload?.percentage ?? 0;
                                return (
                                  <div className="flex items-center justify-between gap-4 min-w-[150px]">
                                    <span className="text-sm">{item.payload?.fullName || name}</span>
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono font-medium text-sm">
                                        {formatCurrency(value as number)}
                                      </span>
                                      <span className="text-muted-foreground text-xs">
                                        ({percentage.toFixed(1)}%)
                                      </span>
                                    </div>
                                  </div>
                                );
                              }}
                            />
                          }
                        />
                        <Pie
                          data={chartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius="60%"
                          outerRadius="90%"
                          strokeWidth={3}
                          stroke="hsl(var(--background))"
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </div>

                {/* Legend */}
                <div className="w-full">
                  <div className="grid grid-cols-1 gap-2">
                    {chartData.slice(0, 5).map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-2.5">
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: item.color }}
                          />
                          <div>
                            <span className="font-medium text-sm">{item.name}</span>
                            {groupBy === "asset" && (
                              <p className="text-xs text-muted-foreground truncate max-w-[120px]">
                                {item.fullName}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="font-mono font-medium text-sm">
                            {formatCompactCurrency(item.value)}
                          </span>
                          <p className="text-xs text-muted-foreground">
                            {item.percentage.toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    ))}
                    {chartData.length > 5 && (
                      <p className="text-xs text-muted-foreground text-center pt-1">
                        +{chartData.length - 5} more
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Card: Sector Allocation (Treemap) */}
            <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold">Sector Allocation</h3>
                  <div className="flex items-center gap-1 px-2 py-1 bg-muted/50 rounded-md">
                    <Grid3X3 className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground font-medium">Treemap</span>
                  </div>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xs text-muted-foreground">
                    {demoSectorAllocationData.length} sectors
                  </span>
                  <span className="text-sm font-semibold tracking-tight">
                    {formatCompactCurrency(demoSectorTotal)}
                  </span>
                </div>
              </div>

              {/* Treemap */}
              <div className="w-full aspect-[4/3] mb-6">
                <ChartContainer config={sectorChartConfig} className="w-full h-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <Treemap
                      data={demoSectorAllocationData}
                      dataKey="size"
                      stroke="hsl(var(--background))"
                      fill="hsl(var(--primary))"
                      content={<CustomTreemapContent x={0} y={0} width={0} height={0} name="" color="" />}
                      isAnimationActive={false}
                    >
                      <ChartTooltip
                        content={<TreemapTooltip />}
                      />
                    </Treemap>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>

              {/* Sector Legend (top 6) */}
              <div className="grid grid-cols-2 gap-2">
                {demoSectorAllocationData.slice(0, 6).map((sector, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded flex-shrink-0"
                        style={{ backgroundColor: sector.color }}
                      />
                      <span className="font-medium text-xs truncate">{sector.name}</span>
                    </div>
                    <span className="font-mono text-xs text-muted-foreground shrink-0 ml-1">
                      {sector.percentage.toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
              {demoSectorAllocationData.length > 6 && (
                <p className="text-xs text-muted-foreground text-center pt-2">
                  +{demoSectorAllocationData.length - 6} more sectors
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
